import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';

import {
  useGetToolDetails,
  useToolAssociatedAgentCount,
} from '@/modules/gpt-chats/hooks/use-tools';
import { useGetAgents } from '@/modules/gpt-chats/hooks/use-agents';
import { IToolSummaryPayload } from '@/modules/gpt-chats/types/tools.service.type';
import React, { useState, useMemo } from 'react';
import { OverviewComponents } from '@/modules/gpt-chats/pages/tools/components/tool-dashboard-overiew-components';
import { ToolDashboardChart } from '@/modules/gpt-chats/pages/tools/components/tool-dashboard-chart/tool-dashboard-chart';
import { Sparkles as CircleSpark } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const ToolDashboard = ({ toolId: toolId }: { toolId: string }) => {
  const { isLoading, data: toolDetails } = useGetToolDetails(toolId, false);
  const { t } = useTranslation();
  const { data: associatedAgentCount } = useToolAssociatedAgentCount(toolId);
  const { data: agentsData } = useGetAgents({ limit: 100, offset: 0 });

  const [selectedAgentId, setSelectedAgentId] = useState<string>('all');
  const [selectedActionId, setSelectedActionId] = useState<string>('all');
  const [period, setPeriod] = useState<'24h' | '7d' | '30d'>('24h');

  const [totalCalls, setTotalCalls] = useState<number>(0);
  const [successfulCalls, setSuccessfulCalls] = useState<number>(0);
  const [failedCalls, setFailedCalls] = useState<number>(0);

  const apis = useMemo(() => {
    if (!toolDetails?.APIConfig?.Actions) return [];
    return Object.entries(toolDetails.APIConfig.Actions).map(([id, action]) => ({
      id,
      name: action.Name,
    }));
  }, [toolDetails]);

  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const start = new Date(now);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);

    if (period === '7d') {
      start.setDate(now.getDate() - 6);
    } else if (period === '30d') {
      start.setDate(now.getDate() - 29);
    }
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, [period]);

  const toolSummaryPayload: IToolSummaryPayload = useMemo(
    () => ({
      tool_id: toolId,
      start_date: startDate,
      end_date: endDate,
      ...(selectedAgentId !== 'all' && { agent_id: selectedAgentId }),
      ...(selectedActionId !== 'all' && { action_id: selectedActionId }),
    }),
    [toolId, selectedAgentId, selectedActionId, startDate, endDate]
  );

  return (
    <div className="relative flex h-auto w-full flex-col gap-6 md:h-[calc(100vh-350px)]">
      <Card className="flex h-auto flex-col overflow-y-auto md:h-full">
        <CardHeader className="gap-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex w-full items-center justify-between lg:w-auto">
              <CardTitle>{t('OVERVIEW')}</CardTitle>
              <div className="flex flex-row gap-2 rounded-sm px-2 lg:hidden">
                <CircleSpark className="aspect-square w-4 p-0 text-cyan-700" />
                <span className="p-0 text-sm text-medium-emphasis">{t('ASSOCIATED_AGENTS')} </span>
                <span className="p-0 text-sm font-medium text-high-emphasis">
                  {associatedAgentCount?.data ?? 0}
                </span>
              </div>
            </div>
            <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2 lg:flex lg:flex-1 lg:flex-row lg:items-center lg:justify-end">
              <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                <SelectTrigger className="w-full lg:w-[180px]">
                  <SelectValue placeholder={t('AGENT_ALL')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('AGENT_ALL')}</SelectItem>
                  {agentsData?.agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedActionId} onValueChange={setSelectedActionId}>
                <SelectTrigger className="w-full lg:w-[180px]">
                  <SelectValue placeholder={t('API_ALL')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('API_ALL')}</SelectItem>
                  {apis.map((api) => (
                    <SelectItem key={api.id} value={api.id}>
                      {api.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={period} onValueChange={(v: any) => setPeriod(v)}>
                <SelectTrigger className="w-full lg:w-[180px]">
                  <SelectValue placeholder={t('SELECT_PERIOD')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">{t('LAST_24_HOURS')}</SelectItem>
                  <SelectItem value="7d">{t('LAST_7_DAYS')}</SelectItem>
                  <SelectItem value="30d">{t('LAST_30_DAYS')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <OverviewComponents
            totalCalls={totalCalls}
            successfulCalls={successfulCalls}
            failedCalls={failedCalls}
            isLoading={isLoading}
          />
          <CardTitle className="p-0">{t('REQUESTS')}</CardTitle>
        </CardHeader>
        <CardContent className="min-h-[300px] flex-1">
          <ToolDashboardChart
            toolSummaryPayload={toolSummaryPayload}
            period={period}
            setTotalCalls={setTotalCalls}
            setSuccessfulCalls={setSuccessfulCalls}
            setFailedCalls={setFailedCalls}
          />
        </CardContent>
      </Card>
    </div>
  );
};
